import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { includes, keyBy, map, some } from 'lodash';

import { images } from 'common/utils';
import { toastError } from 'common/toast';
import { fetchApplicationsFormsSchemas } from 'api/applications-form-schema';

import { APIKeys } from 'common/enum/api-keys.enum';
import { ApplicationFormSchema } from 'common/types/apps-store/application-form-schema';
import {
  ApplicationFormSchemaFields,
  ApplicationFormSchemaCategories,
  ApplicationFormSchemaGraphicsFields,
} from 'common/enum/apps-store/application-form-schema';

export const useApplicationsFormsSchemas = ({
  token,
  tenantName,
  isRootLogin,
}: {
  token: string;
  tenantName: string;
  isRootLogin: boolean;
}) => {
  const [isMdmConnected, setIsMdmConnected] = useState<boolean>(false);
  const { data, refetch, isFetching } = useQuery<ApplicationFormSchema[]>({
    placeholderData: [],
    queryFn: () => fetchData(),
    refetchOnWindowFocus: false,
    queryKey: [APIKeys.APPLICATIONS_FORMS_SCHEMAS],
  });

  const lightModeCategories = [
    ApplicationFormSchemaCategories.MDM,
    ApplicationFormSchemaCategories.DATA,
    ApplicationFormSchemaCategories.SHADOW_SAAS,
  ];

  const fetchData = async (): Promise<ApplicationFormSchema[]> => {
    try {
      const { content } = await fetchApplicationsFormsSchemas({
        token,
        ...(isRootLogin && { tenantName }),
      });

      setIsMdmConnected(content.is_mdm_connected);

      const applicationsFormsSchemas = map(
        content.applications_forms_schemas,
        (schema: ApplicationFormSchema): ApplicationFormSchema => {
          const graphics = schema[ApplicationFormSchemaFields.GRAPHICS];
          const categories = schema[ApplicationFormSchemaFields.CATEGORIES];
          const icon = graphics[ApplicationFormSchemaGraphicsFields.ICON];
          const logo = graphics[ApplicationFormSchemaGraphicsFields.LOGO];
          const isLiteModeApplication = some(
            lightModeCategories,
            (lightModeCategory: ApplicationFormSchemaCategories): boolean =>
              includes(categories, lightModeCategory),
          );

          const isPremium = !isLiteModeApplication;
          const resolvedGraphicsSchema = {
            ...schema,
            [ApplicationFormSchemaFields.IS_PREMIUM]: isPremium,
            [ApplicationFormSchemaFields.GRAPHICS]: {
              [ApplicationFormSchemaGraphicsFields.ICON]: images[icon],
              [ApplicationFormSchemaGraphicsFields.LOGO]: images[logo],
            },
          };

          return resolvedGraphicsSchema;
        },
      );

      return applicationsFormsSchemas;
    } catch (error: any) {
      toastError('Failed to fetch applications schemas', error);

      throw error;
    }
  };

  const applicationTypeToSchema = keyBy(data, ApplicationFormSchemaFields.TYPE);
  const applicationDisplayNameToSchema = keyBy(
    data,
    ApplicationFormSchemaFields.DISPLAY_NAME,
  );

  return {
    data,
    fetch: refetch,
    isMdmConnected,
    isLoading: isFetching,
    applicationTypeToSchema,
    applicationDisplayNameToSchema,
  };
};
